import { useState, type ReactNode } from 'react';
import { CircleAlert, ExternalLink, KeyRound } from 'lucide-react';
import { Banner, Button, CopyField, Dialog, Fact, Facts } from '@/components/ui';
import { useRevealBootstrapCredential } from '@/hooks/useSites';
import { errorText } from '@/utils/errors';
import { siteUrl } from '@/utils/siteUrl';
import type { BootstrapCredential as Credential, SiteDetail } from '@/types/api';

/**
 * The first administrator's password, shown once (TZ section 10.3, step 15).
 *
 * Two deliberate clicks: one to say "show it", which spends it, and the dialog
 * that holds it until the operator closes it. The value is never put back into
 * the query cache, so it does not survive a navigation or linger in memory
 * tooling that inspects cached responses.
 */
export function BootstrapCredentialBanner({ site }: { readonly site: SiteDetail }): ReactNode {
  const [credential, setCredential] = useState<Credential | null>(null);

  const reveal = useRevealBootstrapCredential(site.id, {
    onSuccess: (value) => {
      // The link is built here rather than taken from the server, so it carries
      // the port a development node serves sites on.
      setCredential({ ...value, adminUrl: site.primaryDomain ? siteUrl(site.primaryDomain, '/wp-admin/') : value.adminUrl });
    },
  });

  return (
    <>
      {site.bootstrapCredentialPending ? (
        <Banner tone="info" icon={KeyRound}>
          <div className="stack stack-tight">
            <span>
              WordPress установлен. Пароль администратора можно просмотреть один раз.
            </span>
            <div className="row-tight">
              <Button size="sm" variant="primary" icon={KeyRound} busy={reveal.isPending} onClick={() => reveal.mutate()}>
                Показать пароль
              </Button>
            </div>
            {reveal.isError ? (
              <p className="field-error" role="alert">
                <CircleAlert size={14} aria-hidden="true" />
                {errorText(reveal.error, 'Не удалось получить пароль')}
              </p>
            ) : null}
          </div>
        </Banner>
      ) : null}

      <Dialog
        open={credential !== null}
        title="Администратор WordPress"
        description="Сохраните пароль: повторно он не показывается."
        onClose={() => setCredential(null)}
        footer={
          <Button variant="primary" onClick={() => setCredential(null)}>
            Пароль сохранён
          </Button>
        }
      >
        {credential ? (
          <div className="stack">
            <Facts>
              <Fact label="Логин">
                <span className="mono">{credential.login}</span>
              </Fact>
              {credential.adminUrl ? (
                <Fact label="Вход">
                  <a className="mono link-external" href={credential.adminUrl} target="_blank" rel="noreferrer noopener">
                    {credential.adminUrl}
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                </Fact>
              ) : null}
            </Facts>
            <CopyField label="Пароль" value={credential.password} />
          </div>
        ) : null}
      </Dialog>
    </>
  );
}
